import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

// ============================================================================
// Types
// ============================================================================

export interface DiscountCode {
    code: string
    discount_type: 'percentage' | 'flat'
    discount_percentage: number | null
    discount_amount: number | null
    active: boolean
    created_at: string
    updated_at: string
}

export interface DiscountConfig {
    is_sale_active: boolean
    system_discount_code: string | null
    custom_codes: DiscountCode[]
}

export interface DiscountConfigUpdate {
    is_sale_active?: boolean
    system_discount_code?: string | null
}

export interface DiscountCodeCreate {
    code: string
    discount_type: 'percentage' | 'flat'
    discount_percentage?: number
    discount_amount?: number
    active?: boolean
}

export interface DiscountValidationRequest {
    code: string
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
}

export interface DiscountValidationResponse {
    valid: boolean
    discount_percentage: number
    final_price: number
    original_price: number
    message: string
}

export interface ActiveSaleInfo {
    is_sale_active: boolean
    system_discount_code: string | null
}

// ============================================================================
// Pricing Types - Single Source of Truth
// ============================================================================

export interface ProductPricingRequest {
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
}

export interface ProductPricing {
    product_id: string
    product_type: string
    product_title: string
    full_price: number
    sale_price: number | null
    display_price: number
    original_price: number
    is_sale_active: boolean
    savings_amount: number
    savings_percentage: number
    system_discount_code: string | null
}

export interface BulkPricingRequest {
    products: ProductPricingRequest[]
}

export interface BulkPricingResponse {
    products: ProductPricing[]
    is_sale_active: boolean
    system_discount_code: string | null
}

export interface FeaturedProductsPricing {
    is_sale_active: boolean
    system_discount_code: string | null
    prompt_engineering_mastery: ProductPricing | null
    ai_tools_accelerator: ProductPricing | null
    ai_essentials_bundle: ProductPricing | null
}

// ============================================================================
// Admin Functions (require authentication)
// ============================================================================

/**
 * Get current discount configuration (admin only)
 */
async function getDiscountConfig(): Promise<ApiResponse<DiscountConfig>> {
    try {
        const response = await getApiInstance().get('/discounts/admin/config')
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error fetching discount config:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch discount configuration'
        }
    }
}

/**
 * Update discount configuration (admin only)
 */
async function updateDiscountConfig(updates: DiscountConfigUpdate): Promise<ApiResponse<DiscountConfig>> {
    try {
        const response = await getApiInstance().put('/discounts/admin/config', updates)
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error updating discount config:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to update discount configuration'
        }
    }
}

/**
 * Add a custom discount code (admin only)
 */
async function addCustomCode(codeData: DiscountCodeCreate): Promise<ApiResponse<DiscountConfig>> {
    try {
        const response = await getApiInstance().post('/discounts/admin/custom-codes', codeData)
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error adding discount code:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to add discount code'
        }
    }
}

/**
 * Delete a custom discount code (admin only)
 */
async function deleteCustomCode(code: string): Promise<ApiResponse<DiscountConfig>> {
    try {
        const response = await getApiInstance().delete(`/discounts/admin/custom-codes/${encodeURIComponent(code)}`)
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error deleting discount code:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to delete discount code'
        }
    }
}

// ============================================================================
// Public Functions (no authentication required)
// ============================================================================

/**
 * Validate a discount code for a specific product
 */
async function validateDiscountCode(
    request: DiscountValidationRequest
): Promise<ApiResponse<DiscountValidationResponse>> {
    try {
        const response = await getApiInstance().post('/discounts/validate', request)
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error validating discount code:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to validate discount code'
        }
    }
}

/**
 * Get active sale information (public)
 * Used to display sale banners on product pages
 */
async function getActiveSaleInfo(): Promise<ApiResponse<ActiveSaleInfo>> {
    try {
        const response = await getApiInstance().get('/discounts/active-sale')
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error fetching active sale info:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch sale information'
        }
    }
}

// ============================================================================
// Pricing Functions - SINGLE SOURCE OF TRUTH FOR ALL PRICING
// ============================================================================

/**
 * Get dynamic pricing for a single product.
 * This is the SINGLE SOURCE OF TRUTH for pricing.
 * All frontend components MUST use this to ensure pricing consistency.
 */
async function getProductPricing(
    productId: string,
    productType: 'course' | 'bundle' | 'digital_product'
): Promise<ApiResponse<ProductPricing>> {
    try {
        const response = await getApiInstance().post('/discounts/pricing', {
            product_id: productId,
            product_type: productType
        })
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error fetching product pricing:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch product pricing'
        }
    }
}

/**
 * Get pricing for multiple products in a single API call.
 * Useful for catalog pages.
 */
async function getBulkPricing(
    products: ProductPricingRequest[]
): Promise<ApiResponse<BulkPricingResponse>> {
    try {
        const response = await getApiInstance().post('/discounts/pricing/bulk', {
            products
        })
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error fetching bulk pricing:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch bulk pricing'
        }
    }
}

/**
 * Get pricing for all homepage featured products in a single call.
 * Optimized endpoint to reduce API calls from homepage.
 */
async function getFeaturedProductsPricing(): Promise<ApiResponse<FeaturedProductsPricing>> {
    try {
        const response = await getApiInstance().get('/discounts/pricing/featured')
        const data = response.data

        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error fetching featured products pricing:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch featured products pricing'
        }
    }
}

// ============================================================================
// Export Service
// ============================================================================

export const discountService = {
    // Admin functions
    getDiscountConfig,
    updateDiscountConfig,
    addCustomCode,
    deleteCustomCode,

    // Public functions
    validateDiscountCode,
    getActiveSaleInfo,

    // Pricing functions - SINGLE SOURCE OF TRUTH
    getProductPricing,
    getBulkPricing,
    getFeaturedProductsPricing,
}
